import {
  useMyVariables,
  useUpdateMyVariable,
} from '@/hooks/queries/variables/variables-v2';
import { useUserStore } from '@/store/use-user-store';
import {
  buildUserVariableUpdatePayload,
  getProfileVariableFieldError,
  getProfileVariableLabel,
  isNumericProfileVariableType,
  type MyVariable,
} from '@/types/variables';
import {
  getProfileFormVariables,
  resolveProfileDisplayNames,
} from '@/utils/profile-variables';
import { Icon } from '@iconify/react';
import {
  Button,
  Checkbox,
  Input,
  ScrollShadow,
  Select,
  SelectItem,
  Switch,
  Textarea,
} from '@nextui-org/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

function orderProfileVariables(variables: MyVariable[]): MyVariable[] {
  const hasSequence = variables.some(
    (variable) => variable.sequence !== undefined && variable.sequence !== null
  );

  if (!hasSequence) {
    return variables;
  }

  return [...variables].sort(
    (a, b) =>
      (a.sequence ?? Number.MAX_SAFE_INTEGER) -
      (b.sequence ?? Number.MAX_SAFE_INTEGER)
  );
}

interface UserProfilePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onOpenMemories?: () => void;
}

export function UserProfilePanel({
  isOpen,
  onClose,
  onSuccess,
  onOpenMemories,
}: UserProfilePanelProps) {
  const { data: variablesData } = useMyVariables();

  const { mutate, isPending } = useUpdateMyVariable();
  const setDisplayFirstName = useUserStore(
    (state) => state.setDisplayFirstName
  );
  const setDisplayLastName = useUserStore((state) => state.setDisplayLastName);
  const user = useUserStore((state) => state.user);

  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const visibleVariables = useMemo(
    () => orderProfileVariables(getProfileFormVariables(variablesData)),
    [variablesData]
  );

  // Normalize API value to string (value can be null, string, or array)
  const normalizeValue = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (Array.isArray(val)) return val.join(',');
    return String(val);
  };

  // Populate form when data loads
  useEffect(() => {
    if (visibleVariables.length > 0) {
      const initial: Record<string, string> = {};
      visibleVariables.forEach((v: any) => {
        initial[v.name] = normalizeValue(v.value);
      });
      setFormValues(initial);
    }
  }, [variablesData]);

  const handleChange = (name: string, value: string, variable?: MyVariable) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (variable && isNumericProfileVariableType(variable.type)) {
      setFieldValidation(variable, value);
    }
  };

  const isVariableRequired = (variable: { required?: boolean }) =>
    variable.required === true;

  const getFieldError = (
    variable: MyVariable,
    value: string
  ): string | undefined => getProfileVariableFieldError(variable, value);

  const getFieldLabel = (variable: { name: string; description?: string }) =>
    getProfileVariableLabel(variable);

  const setFieldValidation = (variable: MyVariable, value: string) => {
    const fieldError = getFieldError(variable, value);

    setErrors((prev) => {
      if (!fieldError) {
        if (!prev[variable.name]) return prev;
        const next = { ...prev };
        delete next[variable.name];
        return next;
      }

      if (prev[variable.name] === fieldError) return prev;
      return { ...prev, [variable.name]: fieldError };
    });
  };

  const handleBlur = (variable: MyVariable, value?: string) => {
    setFieldValidation(variable, value ?? formValues[variable.name] ?? '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    visibleVariables.forEach((v: any) => {
      const fieldError = getFieldError(v, formValues[v.name] ?? '');
      if (fieldError) {
        newErrors[v.name] = fieldError;
      }
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    mutate(buildUserVariableUpdatePayload(visibleVariables, formValues), {
      onSuccess: () => {
        const updatedVariables = visibleVariables.map((variable) => ({
          ...variable,
          value: formValues[variable.name] ?? '',
        }));
        const { firstName, lastName } = resolveProfileDisplayNames(
          updatedVariables,
          user
        );
        setDisplayFirstName(firstName);
        setDisplayLastName(lastName);
        onSuccess?.();
        onClose();
      },
    });
  };

  const renderField = (variable: any) => {
    const value = formValues[variable.name] ?? '';
    const error = errors[variable.name];
    const isRequired = isVariableRequired(variable);
    const label = getFieldLabel(variable);
    // Only show description when it adds info beyond the field name
    const rawDesc: string = variable.description ?? '';
    const description: string | undefined =
      rawDesc &&
      rawDesc.toLowerCase() !== variable.name.toLowerCase() &&
      rawDesc.toLowerCase() !== label.toLowerCase()
        ? rawDesc
        : undefined;

    switch (variable.type) {
      case 'boolean':
        return (
          <div key={variable.name} className="flex flex-col gap-1">
            <Switch
              size="sm"
              isSelected={value === 'true'}
              onValueChange={(checked) => {
                const nextValue = String(checked);
                handleChange(variable.name, nextValue);
                setFieldValidation(variable, nextValue);
              }}
            >
              <span className="text-sm font-medium">{label}</span>
            </Switch>
            {/* {description && (
              <p className="text-xs text-gray-500">{description}</p>
            )} */}
            {error && <p className="text-xs text-danger">{error}</p>}
          </div>
        );

      case 'single_select':
        return (
          <Select
            key={variable.name}
            label={label}
            description={description}
            isRequired={isRequired}
            isInvalid={!!error}
            errorMessage={error}
            selectedKeys={value ? [value] : []}
            onSelectionChange={(keys) => {
              const nextValue = (Array.from(keys)[0] as string) ?? '';
              handleChange(variable.name, nextValue);
              setFieldValidation(variable, nextValue);
            }}
            onClose={() => handleBlur(variable)}
          >
            {(variable.props?.options ?? []).map((opt: string) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </Select>
        );

      case 'list': {
        const selected = value ? value.split(',').filter(Boolean) : [];
        return (
          <div key={variable.name} className="flex flex-col gap-1">
            <span className="text-sm font-medium">{label}</span>
            {description && (
              <p className="text-xs text-gray-500">{description}</p>
            )}
            <div
              className="flex flex-col gap-1 pl-1"
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  handleBlur(variable);
                }
              }}
            >
              {(variable.props?.options ?? []).map((opt: string) => (
                <Checkbox
                  key={opt}
                  isSelected={selected.includes(opt)}
                  onValueChange={(checked) => {
                    const updated = checked
                      ? [...selected, opt]
                      : selected.filter((o) => o !== opt);
                    const nextValue = updated.join(',');
                    handleChange(variable.name, nextValue);
                    setFieldValidation(variable, nextValue);
                  }}
                >
                  {opt}
                </Checkbox>
              ))}
            </div>
            {error && <p className="text-xs text-danger">{error}</p>}
          </div>
        );
      }

      case 'integer':
      case 'float':
        return (
          <Input
            key={variable.name}
            label={label}
            description={description}
            placeholder={`Enter ${label.toLowerCase()}`}
            isRequired={isRequired}
            isInvalid={!!error}
            errorMessage={error}
            inputMode={variable.type === 'float' ? 'decimal' : 'numeric'}
            value={value}
            onChange={(e) =>
              handleChange(variable.name, e.target.value, variable)
            }
            onBlur={(e) =>
              handleBlur(variable, (e.target as HTMLInputElement).value)
            }
          />
        );

      default:
        // text and anything else
        return (description?.length ?? 0) > 80 ? (
          <Textarea
            key={variable.name}
            label={label}
            description={description}
            placeholder={`Enter ${label.toLowerCase()}`}
            isRequired={isRequired}
            isInvalid={!!error}
            errorMessage={error}
            value={value}
            onChange={(e) => handleChange(variable.name, e.target.value)}
            onBlur={(e) =>
              handleBlur(variable, (e.target as HTMLTextAreaElement).value)
            }
            minRows={3}
          />
        ) : (
          <Input
            key={variable.name}
            label={label}
            description={description}
            placeholder={`Enter ${label.toLowerCase()}`}
            isRequired={isRequired}
            isInvalid={!!error}
            errorMessage={error}
            value={value}
            onChange={(e) => handleChange(variable.name, e.target.value)}
            onBlur={(e) =>
              handleBlur(variable, (e.target as HTMLInputElement).value)
            }
          />
        );
    }
  };

  // Use portal to render at document.body level, escaping header's stacking context
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - click to close */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[9999]"
          />

          {/* Panel - slides in from right with modal-like appearance */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed top-3 right-3 bottom-3 w-[calc(100%-1.5rem)] sm:w-[400px] bg-white z-[10000] flex flex-col shadow-2xl rounded-2xl overflow-hidden"
            style={{
              paddingTop: 'env(safe-area-inset-top, 0px)',
            }}
          >
            {/* Header */}
            <div className="flex justify-between items-center px-4 py-3 border-b shrink-0">
              <span className="text-lg font-semibold">User Profile</span>
              <Button isIconOnly variant="light" onPress={onClose} size="sm">
                <Icon icon="mdi:close" className="h-5 w-5" />
              </Button>
            </div>

            {/* Scrollable Content */}
            <ScrollShadow className="flex-1 overflow-y-auto">
              {/* My Memories link */}
              {onOpenMemories && (
                <div className="px-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onOpenMemories();
                    }}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors w-full py-2.5 px-3 rounded-lg hover:bg-blue-50"
                  >
                    <Icon icon="mdi:brain" className="w-4.5 h-4.5" />
                    <span>My Memories</span>
                    <Icon
                      icon="mdi:chevron-right"
                      className="w-4 h-4 ml-auto"
                    />
                  </button>
                </div>
              )}
              <form
                id="user-profile-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-4 p-4"
              >
                {visibleVariables.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">
                    No profile variables configured.
                  </p>
                ) : (
                  visibleVariables.map((v: any) => renderField(v))
                )}
              </form>
            </ScrollShadow>

            {/* Fixed Footer */}
            <div
              className="px-4 py-4 border-t bg-white shrink-0 rounded-b-2xl"
              style={{
                paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1rem)',
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="bordered"
                  onPress={onClose}
                  isDisabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  form="user-profile-form"
                  isLoading={isPending}
                >
                  Save Profile
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
